"use client";

import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { TimelineIcon } from "@/components/Atoms/Vectors";
import { useState } from "react";
import RangeSlider from "@/components/Atoms/RangeSlider";
import styles from "./index.module.scss";

function getAssetSrc(asset?: {
  responsiveImage?: { src?: string };
  url?: string;
}) {
  return asset?.responsiveImage?.src || asset?.url || "";
}

function getAssetAlt(asset?: {
  responsiveImage?: { alt?: string };
  alt?: string;
}) {
  return asset?.responsiveImage?.alt || asset?.alt || "";
}

interface TimelineRangerProps {
  title: string;
  titleColor: any;
  yearColor: any;
  textColor: any;
  historyMoments: any[];
}

export const TimelineRange = ({
  historyMoments,
  textColor,
  title,
  titleColor,
  yearColor,
}: TimelineRangerProps) => {
  const [historyMoment, setHistoryMoment] = useState(
    historyMoments ? historyMoments[0] : false
  );

  const [rangeValue, setRangeValue] = useState(0);

  const onRangeChange = (e: any) => {
    const index = parseInt(e.target.value);
    const selectedMoment = historyMoments[index];

    if (selectedMoment) {
      setRangeValue(index);
      setHistoryMoment(selectedMoment);
    }
  };

  const activeYear = historyMoment.addRangeOfYears
    ? historyMoment.rangeYear
    : historyMoment.year;

  const resolvedYearColor = yearColor?.hex || textColor?.hex;
  const resolvedDotColor = titleColor?.hex || textColor?.hex;
  const mainAssetSrc = getAssetSrc(historyMoment.image);
  const companionAssetSrc = getAssetSrc(historyMoment.companionImage);
  const mainAssetAlt = getAssetAlt(historyMoment.image);

  return (
    <div className="flex flex-col gap-8 lg:gap-10">
      <div className="w-full">
        <MultiPurposeTitle
          tag="h3"
          type="default"
          data={title}
          color={titleColor || textColor}
          className="lg:!text-[48px] lg:!leading-[56px]"
        />
      </div>

      <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start lg:gap-x-20">
        <div className="flex flex-col gap-6 lg:col-span-4 lg:gap-8">
          <h3
            className="brandTimeline text-[4rem] leading-none tracking-[2px] sm:text-[5rem] lg:text-[136px] lg:leading-[80px]"
            style={{ color: resolvedYearColor }}
          >
            {activeYear}
          </h3>
          <div className="[&_p]:!text-[20px] [&_p]:!leading-[32px] [&_p]:!tracking-[0.04px]">
            <MultiPurposeParagraph
              color={textColor}
              data={historyMoment.description}
            />
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:col-span-8 lg:gap-8">
          <div className="relative w-full max-w-[608px] pb-14 lg:pb-10">
            <TimelineIcon
              className="absolute"
              moments={historyMoments}
              color={resolvedDotColor}
            />
            <RangeSlider
              className="absolute -bottom-14 left-2 w-full md:-bottom-14 md:left-2 lg:-bottom-7 lg:left-0"
              min="0"
              max={(historyMoments.length - 1).toString()}
              value={rangeValue}
              onChange={onRangeChange}
              style={{ "--thumbColor": resolvedDotColor } as React.CSSProperties}
            />
          </div>

          {mainAssetSrc && (
            <div className={styles.assets}>
              {companionAssetSrc && (
                <div
                  className={`${styles.assetLayer} ${styles.assetLayerCompanion}`}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={companionAssetSrc}
                    alt=""
                    aria-hidden
                    className={styles.assetImg}
                    loading="lazy"
                  />
                </div>
              )}
              <div className={`${styles.assetLayer} ${styles.assetLayerMain}`}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={mainAssetSrc}
                  alt={mainAssetAlt}
                  className={styles.assetImg}
                  loading="lazy"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
